import { h, Component, options as preactOptions } from 'preact';
import { setMany as idbSetMany, getMany as idbGetMany, clear as idbClear } from 'idb-keyval';
import Store from 'store/store';
import pendingPromise from 'utils/pendingPromise';
import { post, request } from 'utils/api';
import { accessNested, joinClasses } from 'utils/utils';
import { applyChanges, clearNewTicketsList, clearStatusChangesList, getChangesList, getNewTicketsList, getStatusChangesList, loadChanges, processTickets, setTicketStatusRaw } from 'utils/tickets';

import Header from 'components/header/header';
import Footer from 'components/footer/footer';
import Login from 'components/login/login';
import Main from 'components/main/main';
import Scanner from 'components/scanner/scanner';
import Search from 'components/search/search';

import s from 'base/primeCheckIn/primeCheckInApp.scss';

let transitionReady = Promise.resolve();
preactOptions.debounceRendering = async (cb) => {
	await transitionReady;
	cb();
};

async function tryToPersistData(allData) {
	try {
		idbSetMany(
			[
				'theater',
				'movie',
				'screening',
				'tickets'
			].filter(k => allData[k]).map(k => [k, allData[k]]).concat([
				['at', Date.now()],
			])
		);
	} catch (err) {
		// ignore
	}
}
async function getPersistedData() {
	try {
		const all = await idbGetMany([
			'theater',
			'movie',
			'screening',
			'tickets',
			'at'
		]);
		const [theater, movie, screening, tickets, at] = all;
		if (all.some(v => !v) || at < Date.now() - 1000 * 60 * 60 * 24) {
			await idbClear();
			return null;
		}
		return { theater, movie, screening, tickets };
	} catch (err) {
		// ignore
	}
	return null;
}

export default class PrimeCheckInApp extends Component {
	constructor(props) {
		super(props);
		this.goTo = this.goTo.bind(this);
		this.goToMain = this.goToMain.bind(this);
		this.showLogin = this.showLogin.bind(this);
		this.onLogIn = this.onLogIn.bind(this);
		this.checkOffline = this.checkOffline.bind(this);
		this.confirmLeaving = this.confirmLeaving.bind(this);
		this.synchronize = this.synchronize.bind(this);
		this.uploadChanges = this.uploadChanges.bind(this);

		Store.set({
			...window.__INITIAL_STATE__,
		});

		this.state = {
			// loadingData: true,
			// loggedIn: false,
			step: 'loading',
			store: Store.get()
		};
		Store.on('update', () => {
			this.setState({ store: Store.get() });
		});

		Store.on('REQUEST_LOGIN', this.showLogin);
	}

	setState(state, callback) {
		const shouldTransition = document.startViewTransition && state && [
			'step',
			'store.cameraState'
		].some(prop => {
			const path = prop.split('.');
			return path[0] in state && accessNested(state, path) !== accessNested(this.state, path);
		});
		if (shouldTransition) {
			this.renderDone?.resolve();
			this.renderDone = pendingPromise();
			transitionReady.resolve?.();
			transitionReady = pendingPromise();
			document.startViewTransition(async () => {
				transitionReady.resolve();
				await this.renderDone;
			});
		}
		return super.setState(state, callback);
	}

	async componentDidMount() {
		this.preventPinchZoom();

		// try {
		// 	screen.orientation?.lock?.('portrait') || (screen.lockOrientation || screen.mozLockOrientation)('portrait');
		// } catch (err) {
		// 	console.log('Could not lock orientation');
		// }
		window.addEventListener('offline', this.checkOffline);
		window.addEventListener('online', this.checkOffline);
		if (!__IS_LOCAL__) {
			window.addEventListener('beforeunload', this.confirmLeaving);
		}

		loadChanges();
		await this.loadData();
	}

	preventPinchZoom() {
		document.addEventListener('gesturestart', function(e) {
			e.preventDefault();
		});
		
		document.addEventListener('gesturechange', function(e) {
			e.preventDefault();
		});
		
		document.addEventListener('gestureend', function(e) {
			e.preventDefault();
		});
	}

	componentDidUpdate() {
		this.renderDone?.resolve();
		this.renderDone = null;
	}

	goTo(step) {
		this.setState({ step });
	}

	goToMain() {
		this.goTo('main');
	}

	showLogin() {
		this.goTo('login');
	}

	async onLogIn() {
		await this.loadData();
	}

	async loadDataRaw() {
		try {
			const data = await request('data/all');
			data.tickets = processTickets(data.tickets);
			// Not awaiting as this should not block and just be done in the background
			tryToPersistData(data);
			return data;
		} catch (err) {
			const code = err?.code;
			if (code !== 404 && code !== 401) {
				const persisted = await getPersistedData();
				if (persisted) {
					return persisted;
				}
			}
			throw err;
		}
	}

	async loadData() {
		this.setState({ step: 'loading', error: null });
		try {
			let { theater, movie, screening, tickets } = await this.loadDataRaw();

			Store.get().set({ theater, movie, screening, tickets });
			applyChanges();
			this.latestTicket = Math.max(...tickets.map(t => t.creation));

			this.goTo('main');
			this.scheduleSync();
		} catch (err) {
			if (err?.code === 404) {
				this.setState({
					step: 'error',
					error: 'No active movie found at your theater'
				});
			} else if (err?.code !== 401) {
				this.setState({
					step: 'error',
					error: 'Unable to load data'
				});
			}
		}
	}

	scheduleSync() {
		clearTimeout(this.nextSync);
		this.nextSync = setTimeout(this.synchronize, 15000);
	}

	checkOffline() {
		const store = Store.get();
		const offline = !navigator.onLine;
		if (store.offline !== offline) {
			store.set({ offline });
		}
		if (offline) {
			clearTimeout(this.nextSync);
		} else {
			this.synchronize();
		}
	}

	async uploadChanges() {
		let { screening, tickets } = this.state.store;
		if (!screening || !tickets) {
			return;
		}
		const statusChanges = getStatusChangesList();
		if (statusChanges.length) {
			const statusUpdateResult = await post('/tickets/status', { data: statusChanges });
			if (statusUpdateResult) {
				clearStatusChangesList();
				tryToPersistData({ tickets });
			}
		}
		const newTicketsLocal = getNewTicketsList();
		tickets = tickets.toJS();
		const newTickets = (await Promise.all(newTicketsLocal.map(async localTicket => {
			const result = await post('/tickets/create', { screening: screening.id, ...localTicket });
			tickets = tickets.filter(t => !localTicket.ids.includes(t.id));
			return result.tickets;
		}))).flat();
		if (newTickets.length) {
			clearNewTicketsList();
			const data = { tickets: processTickets(tickets.concat(newTickets)) };
			Store.get().set(data);
			tryToPersistData(data);
		}
	}

	async synchronize() {
		const { step } = this.state;
		if (step === 'loading' || step === 'login') {
			return this.scheduleSync();
		}
		clearTimeout(this.nextSync);
		try {
			const remoteChanges = await request('/status/update', { qs: { since: this.latestTicket || 0 } });
			if (Array.isArray(remoteChanges?.status)) {
				remoteChanges.status.forEach(({ id, status }) => setTicketStatusRaw(id, status));
			}
			if (Array.isArray(remoteChanges?.tickets) && remoteChanges.tickets.length) {
				const data = { tickets: processTickets((Store.get().tickets || []).concat(remoteChanges.tickets)) };
				Store.get().set(data);
				this.latestTicket = Math.max(this.latestTicket, ...data.tickets.map(t => t.creation));
				tryToPersistData(data);
			}

			await this.uploadChanges();
		} catch (e) {
			console.error(e);
		}
		this.scheduleSync();
	}

	confirmLeaving(e) {
		if (!getChangesList().length) {
			return;
		}
		let message = 'Are you sure you want to leave this page?';
		if (e) {
			e.returnValue = message;
		}
		return message;
	}

	render(props, state) {
		const { store, step, error } = state;
		const { appData, offline, theater, movie, screening, tickets, cameraState, camera } = store;

		const content = {
			loading: <div class={s.loading} />,
			login: <Login onLogIn={this.onLogIn} />,
			error: <div class={s.error}>{error}</div>,
			main: (
				<Main
					goTo={this.goTo}
					offline={offline}
					theater={theater}
					movie={movie}
					screening={screening}
					tickets={tickets}
				/>
			),
			scan: (
				<Scanner
					goTo={this.goTo}
					offline={offline}
					theater={theater}
					movie={movie}
					screening={screening}
					tickets={tickets}
					cameraState={cameraState}
					camera={camera}
				/>
			),
			search: (
				<Search
					goTo={this.goTo}
					offline={offline}
					theater={theater}
					movie={movie}
					screening={screening}
					tickets={tickets}
					synchronize={this.synchronize}
				/>
			),
		}[state.step];

		const mediaRoot = appData?.urls?.roots?.media;

		return (
			<div class={joinClasses(s.root, s[`step-${step}`])}>
				<audio src={`${mediaRoot}/audio/success.mp3`} id="sound-success" preload="auto" />
				<audio src={`${mediaRoot}/audio/error.mp3`} id="sound-error" preload="auto" />
				<Header
					showSignOut={step !== 'loading' && step !== 'login'}
					hideTitle={step === 'scan'}
					backAction={(step === 'scan' || step === 'search') && this.goToMain}
					uploadChanges={this.uploadChanges}
				/>
				<div class={s.content}>
					{content}
				</div>
				<Footer />
			</div>
		);
	}
}
